
import { useTranslation } from 'react-i18next';
import { useEffect, useCallback } from 'react';
import CardItemView from '@/components/CardItemView';
import Select from '@/components/Select';
import Sort from '@/components/Sort';
import Button from '@/components/Button';
import { CardSkeleton, HotCardsFiltersSkeleton } from '@/components/Skeleton';

const HotCardsSection = ({
  cards,
  brands,
  categories,
  selectedBrand,
  selectedCategory,
  onBrandChange,
  onCategoryChange,
  onSortChange,
  onLoadMore,
  hasMoreCards,
  isLoading,
  isLoadingMore,
  isFiltersLoading,
  pagesLoaded
}) => {
  const { t } = useTranslation();

  const handleScroll = useCallback(() => {
    if (isLoadingMore || !hasMoreCards || pagesLoaded >= 1) return;
    
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const windowHeight = window.innerHeight;
    const docHeight = document.documentElement.scrollHeight;
    
    // Trigger load more when user scrolls to within 200px of the bottom
    if (scrollTop + windowHeight >= docHeight - 200) {
      onLoadMore();
    }
  }, [isLoadingMore, hasMoreCards, onLoadMore, pagesLoaded]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  // Check scroll position after loading completes
  useEffect(() => {
    if (!isLoadingMore && hasMoreCards && cards.length > 0 && pagesLoaded < 3) {
      // Small delay to ensure DOM is updated
      const timer = setTimeout(() => {
        handleScroll();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isLoadingMore, hasMoreCards, cards.length, pagesLoaded, handleScroll]);
  
  // If initial loading, show skeleton cards but keep filters visible
  const showSkeleton = isLoading && cards.length === 0;
  const showFilterSkeleton = isFiltersLoading;

  return (
    <div className="mb-12">
      <h2 className="text-2xl font-bold text-[#111111] mb-4">{t('home.sections.hot_cards')}</h2>

      {showFilterSkeleton ? (
        <HotCardsFiltersSkeleton />
      ) : (
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-4">
            <Select
              value={selectedBrand}
              onValueChange={onBrandChange}
              options={brands}
              className="w-48"
            />
            <Select
              value={selectedCategory}
              onValueChange={onCategoryChange}
              options={categories}
              className="w-48"
            />
          </div>

          <Sort
            items={[
              { label: t('home.filters.sort.release_date'), value: 'date' },
              { label: t('home.filters.sort.price'), value: 'price' },
              { label: t('home.filters.sort.popularity'), value: 'popularity' },
            ]}
            onChange={onSortChange}
            defaultSortIndex={2}
            defaultDirection="desc"
          />
        </div>
      )}

      <div className="relative">
        {showSkeleton ? (
          <div className="grid grid-cols-5 max:grid-cols-6 gap-4">
            {Array.from({ length: 30 }, (_, i) => (
              <CardSkeleton key={`skeleton-${i}`} />
            ))}
          </div>
        ) : cards.length === 0 && !isLoading ? (
          <div className="flex justify-center items-center h-64">
            <p>No cards found matching your criteria.</p>
          </div>
        ) : (
          <div className="grid grid-cols-5 max:grid-cols-6 gap-4">
            {cards.map((card) => (
              <CardItemView
                key={card.id}
                id={card.id}
                type={card.type === 'SINGLE_CARD' ? 'PSA_SINGLE_CARD' : 'CARD_BOX'}
                name={card.localizedRawName}
                image={card.images?.[0]?.url}
                brand={card.brand?.localizedName}
                price={(() => {
                  const vals = [
                    card.stats?.lowestPriceOnSale,
                    card.stats?.lowestPriceOnSaleOfficial,
                  ].filter(v => typeof v === 'number' && v > 0);
                  return vals.length ? Math.min(...vals) : 0;
                })()}
                orders={card.stats?.salesVolume}
                code={card.code}
                rawCode={card.rawCode}
                set={card.set}
                rarityInfo={card.rarityInfo}
                trackEventName="popular_card_click"
              />
            ))}
            
            {/* Show skeleton cards in the same grid when loading more */}
            {isLoadingMore && Array.from({ length: 40 }, (_, i) => (
              <CardSkeleton key={`skeleton-${i}`} />
            ))}
          </div>
        )}
        
        {/* Show load more button after 3 pages */}
        {pagesLoaded >= 1 && hasMoreCards && !isLoadingMore && (
          <div className="flex justify-center mt-8">
            <Button
              onClick={onLoadMore}
              variant="default"
              size="l"
            >
              {t('home.actions.load_more')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default HotCardsSection; 